import React, { useState, useEffect } from 'react';
import { Plus, Briefcase, Edit, Trash2 } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { proveedoresService } from '../services/api';
import { TIPOS_PROVEEDOR } from '../utils/constants';
import toast from 'react-hot-toast';

const ProveedoresPage = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nombre: '', ruc: '', tipo: 'SERVICIOS', contacto: '', telefono: '', email: '', direccion: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await proveedoresService.getAll();
      setProveedores(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await proveedoresService.update(selected.id, form);
        toast.success('Proveedor actualizado');
      } else {
        await proveedoresService.create(form);
        toast.success('Proveedor creado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (prov) => {
    setSelected(prov);
    setForm({ nombre: prov.nombre, ruc: prov.ruc || '', tipo: prov.tipo, contacto: prov.contacto || '', telefono: prov.telefono || '', email: prov.email || '', direccion: prov.direccion || '' });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await proveedoresService.delete(selected.id);
      toast.success('Proveedor eliminado');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({ nombre: '', ruc: '', tipo: 'SERVICIOS', contacto: '', telefono: '', email: '', direccion: '' });
  };

  const columns = [
    { header: 'Nombre', accessor: 'nombre' },
    { header: 'RUC', render: (row) => row.ruc || '-' },
    { header: 'Tipo', render: (row) => <Badge variant="info">{TIPOS_PROVEEDOR.find(t => t.value === row.tipo)?.label || row.tipo}</Badge> },
    { header: 'Contacto', render: (row) => row.contacto || '-' },
    { header: 'Teléfono', render: (row) => row.telefono || '-' },
    { header: 'Acciones', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"><Edit size={16} /></button>
        <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"><Trash2 size={16} /></button>
      </div>
    )}
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Proveedores</h1>
          <p className="text-slate-400">Gestión de proveedores de servicios</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>Nuevo Proveedor</Button>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20"><Briefcase className="text-blue-400" size={20} /></div>
          <div>
            <p className="text-sm text-slate-400">Total Proveedores</p>
            <p className="text-xl font-bold text-white">{proveedores.length}</p>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table columns={columns} data={proveedores} loading={loading} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Proveedor' : 'Nuevo Proveedor'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />
            <Input label="RUC" value={form.ruc} onChange={(e) => setForm({ ...form, ruc: e.target.value })} />
            <Select label="Tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} options={TIPOS_PROVEEDOR} />
            <Input label="Contacto" value={form.contacto} onChange={(e) => setForm({ ...form, contacto: e.target.value })} />
            <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <Input label="Dirección" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Guardar'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Proveedor" />
    </div>
  );
};

export default ProveedoresPage;
